import { z } from "zod";
import { zonedInstant, zonedParts } from "./zones";

/** Working hours (the working hours round, 2026-11-01; brief docs/briefs/working-hours-round.md, rules 2 to 6, DECISIONS "2026-11-01 — Working
 *  hours per person"; the hours editor round, 2026-11-02, brief docs/briefs/discussion-polish-hours-editor-round.md rule 7). In plain words: a
 *  week of hours holds a list of *blocks* a day ("09:00 to 12:00" and "13:00 to 17:00"), an empty list is a day off; the workspace has a
 *  *default* for everyone without hours of their own, and each person may have their own, always read in that person's own time zone. The
 *  hours drive one thing: holding notifications outside them (`mail.ts`). Pure, shared by the screen, the API and the notifier. */

export const DAY_KEYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;
export type DayKey = (typeof DAY_KEYS)[number];

/** "HH:MM" in 30-minute steps (the picker's steps, so a typed "09:15" never reaches the hold) */
const HALF_HOUR = /^([01]\d|2[0-4]):(00|30)$/;
export const minutesOfTime = (hhmm: string): number => { const [h, m] = hhmm.split(":").map(Number); return (h ?? 0) * 60 + (m ?? 0); };
export const timeOfMinutes = (minutes: number): string => `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
const DAY_END = 24 * 60;
/** at most four blocks a day (the hours editor round's rule 7) */
export const MAX_BLOCKS_A_DAY = 4;

/** one block: from 00:00 to 23:30, to 00:30 to 24:00, from before to */
export const WorkingBlockSchema = z.object({ from: z.string().regex(HALF_HOUR), to: z.string().regex(HALF_HOUR) }).strict()
  .refine((b) => minutesOfTime(b.from) < DAY_END && minutesOfTime(b.to) <= DAY_END && minutesOfTime(b.from) < minutesOfTime(b.to), { message: "A block starts before it ends, within the day" });
export type WorkingBlock = z.infer<typeof WorkingBlockSchema>;
/** blocks in time order that never overlap (two that touch at 12:00 stay two; DECISIONS "2026-11-02 — Touching blocks") */
const inOrder = (blocks: WorkingBlock[]) => blocks.every((b, i) => i === 0 || minutesOfTime(blocks[i - 1]!.to) <= minutesOfTime(b.from));
const day = () => z.array(WorkingBlockSchema).max(MAX_BLOCKS_A_DAY).refine(inOrder, { message: "Blocks run in time order and never overlap" }).default([]);
/** the week; `v: 2` marks the list shape, so a stored week of the old one-block shape never parses (DECISIONS "2026-11-02 — The hours shape v: 2") */
export const WorkingHoursSchema = z.object({
  v: z.literal(2).default(2),
  days: z.object({ Mon: day(), Tue: day(), Wed: day(), Thu: day(), Fri: day(), Sat: day(), Sun: day() }).strict(),
}).strict();
export type WorkingHours = z.infer<typeof WorkingHoursSchema>;

const nineToFive = (): WorkingBlock[] => [{ from: "09:00", to: "17:00" }];
/** the default for a new workspace: Monday to Friday 09:00 to 17:00 (the working hours round's question 1) */
export const DEFAULT_WORKING_HOURS: WorkingHours = { v: 2, days: { Mon: nineToFive(), Tue: nineToFive(), Wed: nineToFive(), Thu: nineToFive(), Fri: nineToFive(), Sat: [], Sun: [] } };
/** a fresh copy of the default (a schema default must never hand out one shared object) */
export const defaultWorkingHours = (): WorkingHours => structuredClone(DEFAULT_WORKING_HOURS);
/** an empty week: every day off */
export const noWorkingHours = (): WorkingHours => ({ v: 2, days: { Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] } });

/** the hours that apply to a person: their own, else the workspace's default */
export const effectiveHours = (own: WorkingHours | null | undefined, workspaceDefault: WorkingHours): WorkingHours => own ?? workspaceDefault;
/** a person's own hours as stored (a row that no longer parses, the old shape included, reads as none: the default applies, never a broken week) */
export function storedHours(raw: unknown): WorkingHours | null {
  if (raw === null || raw === undefined) return null;
  const parsed = WorkingHoursSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

const blockMinutes = (b: WorkingBlock) => minutesOfTime(b.to) - minutesOfTime(b.from);
const dayMinutes = (blocks: WorkingBlock[]) => blocks.reduce((sum, b) => sum + blockMinutes(b), 0);
export const weeklyMinutes = (h: WorkingHours): number => DAY_KEYS.reduce((sum, d) => sum + dayMinutes(h.days[d]), 0);
/** the weekly total as a person says it: "40 h", "37.5 h", "0 h" */
export const weeklyHoursLabel = (h: WorkingHours): string => `${weeklyMinutes(h) / 60} h`;
export const hasWorkingBlock = (h: WorkingHours): boolean => DAY_KEYS.some((d) => h.days[d].length > 0);

/** "A", "A and B", "A, B and C" */
export const joinWithAnd = (parts: string[]): string => parts.length <= 1 ? (parts[0] ?? "") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
const blocksLabel = (blocks: WorkingBlock[]) => joinWithAnd(blocks.map((b) => `${b.from} to ${b.to}`));

/** the hours in a few words: consecutive days with the same blocks form one group ("Mon to Fri, 09:00 to 12:00 and 13:00 to 17:00"), groups
 *  joined with "; " */
export function hoursSummary(h: WorkingHours): string {
  const groups: { first: DayKey; last: DayKey; label: string }[] = [];
  let open: (typeof groups)[number] | null = null;
  for (const d of DAY_KEYS) {
    const blocks = h.days[d];
    if (!blocks.length) { open = null; continue; }
    const label = blocksLabel(blocks);
    if (open && open.label === label) { open.last = d; continue; }
    open = { first: d, last: d, label }; groups.push(open);
  }
  if (!groups.length) return "No working hours";
  return groups.map((g) => `${g.first === g.last ? g.first : `${g.first} to ${g.last}`}, ${g.label}`).join("; ");
}

/** who sees which lane, which weekly number and which Edit on the Working hours tab (rule 2): one rule for the screen and the server. A
 *  person always sees their own lane and number, and edits their own while "Members set their own hours" is on; the team right sees and edits
 *  everyone; a member sees a colleague's lane only while "Members see each other's hours" is on, never a colleague's number or Edit. The
 *  number is a screen rule only: once the lanes are visible, the blocks could be added up (the red team's finding 5). */
export type HoursAccess = { lane: boolean; total: boolean; edit: boolean };
export function hoursAccess(o: { viewer: { memberId: string; team: boolean }; target: { memberId: string }; hoursVisible: boolean; hoursSelfSet: boolean }): HoursAccess {
  if (o.viewer.team) return { lane: true, total: true, edit: true };
  if (o.viewer.memberId === o.target.memberId) return { lane: true, total: true, edit: o.hoursSelfSet };
  return { lane: o.hoursVisible, total: false, edit: false };
}

/** a stretch of one day column, in minutes from the viewer's midnight */
export type DaySpan = { from: number; to: number };
const DAY_MS = 86_400_000;
const emptyWeek = (): Record<DayKey, DaySpan[]> => ({ Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [], Sun: [] });
/** the UTC midnight of the calendar Monday of the week that holds `at` in the zone (a calendar date, not an instant) */
function mondayOf(at: Date, zone: string): number {
  const p = zonedParts(at, zone);
  const index = DAY_KEYS.indexOf(p.weekday as DayKey);
  return Date.UTC(p.y, p.m - 1, p.d - Math.max(0, index));
}
/** the instant of a wall-clock minute on the calendar date `dayMs` (a UTC midnight) in a zone */
const instantOn = (dayMs: number, minutes: number, zone: string) => { const d = new Date(dayMs); return zonedInstant(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate(), Math.floor(minutes / 60), minutes % 60, zone).getTime(); };
/** which day of the viewer's week an instant falls on (may be -1 or 7 at the edges) and its minute there */
function viewerSpot(t: number, monday: number, zone: string): { index: number; minute: number } {
  const q = zonedParts(new Date(t), zone);
  return { index: Math.round((Date.UTC(q.y, q.m - 1, q.d) - monday) / DAY_MS), minute: q.hh * 60 + q.mm };
}
const columnOf = (index: number): DayKey => DAY_KEYS[((index % 7) + 7) % 7]!;

/** one block of the person's day `date`, cut at the viewer's midnights into the columns it covers */
function placeBlock(out: Record<DayKey, DaySpan[]>, b: WorkingBlock, date: number, o: { monday: number; personZone: string; viewerZone: string }): void {
  let cur = instantOn(date, minutesOfTime(b.from), o.personZone);
  const end = instantOn(date, minutesOfTime(b.to), o.personZone);
  while (cur < end) {
    const spot = viewerSpot(cur, o.monday, o.viewerZone);
    const midnight = instantOn(o.monday + (spot.index + 1) * DAY_MS, 0, o.viewerZone);
    const stop = Math.min(end, midnight);
    if (stop <= cur) break; // a clock that jumps backwards at midnight: never loop
    out[columnOf(spot.index)].push({ from: spot.minute, to: stop === midnight ? DAY_END : viewerSpot(stop, o.monday, o.viewerZone).minute });
    cur = stop;
  }
}

/** Someone's hours drawn in the viewer's zone for the week that holds `at` (the week's dates decide the offsets, so daylight saving follows
 *  the week shown): each day's stretches in minutes from the viewer's midnight, sorted by start. A block that crosses the viewer's midnight
 *  continues at the top of the next column (every block of a day, the hours editor round); past Sunday it continues in Monday's column, and a backward spill from Monday lands in Sunday's
 *  (the red team's finding 17). */
export function shiftWeek(h: WorkingHours, personZone: string, viewerZone: string, at: string | Date): Record<DayKey, DaySpan[]> {
  const out = emptyWeek();
  const monday = mondayOf(new Date(at), viewerZone);
  DAY_KEYS.forEach((d, i) => {
    const date = monday + i * DAY_MS;
    for (const b of h.days[d]) placeBlock(out, b, date, { monday, personZone, viewerZone });
  });
  for (const d of DAY_KEYS) out[d].sort((a, b) => a.from - b.from);
  return out;
}
